import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom'; // Import Link for navigation
import AssetValueChart from './components/AssetValueChart';

const AssetsPage = () => {
    const [physicalAssets, setPhysicalAssets] = useState([]);
    const [cashAccounts, setCashAccounts] = useState([]); // open cash accounts with latest balance
    const [investedAccounts, setInvestedAccounts] = useState([]); // open invested accounts with latest value
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('tiles'); // 'table' | 'tiles'
    const [expanded, setExpanded] = useState({}); // propertyId -> bool
    const [series, setSeries] = useState([]); // unified time-series for chart
    const [period, setPeriod] = useState('YTD'); // '1M' | '3M' | '6M' | '1Y' | 'YTD' | 'ALL'
    const [deltaMode, setDeltaMode] = useState('%'); // '%' | '$'

    const getAssetUrl = (type, id, parentId=null) => {
        switch (type) {
            case 'resi_property':
                return `/balance_sheet/assets/physical_assets/resi_properties/${id}`;
            case 'land':
                return `/balance_sheet/assets/physical_assets/resi_properties/land/${id}`;
            case 'structure':
                return `/balance_sheet/assets/physical_assets/resi_properties/structures/${id}`;
            case 'vehicle':
                return `/balance_sheet/assets/physical_assets/vehicles/${id}`;
            default:
                return `/balance_sheet/assets/physical_assets/unknown/${id}`; // Fallback for unknown types
        }
    };

    const getAssetTypeUrl = (type) => {
        switch (type) {
            case 'resi_property':
                return `/balance_sheet/assets/physical_assets/resi_properties/`;
            case 'vehicle':
                return `/balance_sheet/assets/physical_assets/vehicles/`;
            case 'structure':
                return `/balance_sheet/assets/physical_assets/resi_properties/structures`;
            case 'land':
                return `/balance_sheet/assets/physical_assets/resi_properties/land`;
            default:
                return `/balance_sheet/assets/physical_assets`;
        }
    };

    const formatCurrency = (val) => {
        if (val === null || val === undefined) return 'N/A';
        let num = null;
        try {
            if (typeof val === 'object' && val.amount !== undefined) {
                num = parseFloat(val.amount);
            } else {
                num = parseFloat(val);
            }
        } catch (e) { /* ignore */ }
        if (isNaN(num)) return 'N/A';
        const rounded = Math.round(num);
        return '$' + rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const getDebtUrl = (debt) => {
        const id = debt?.id;
        const type = debt?.debt_type;
        if (!id || !type) return null;
        switch (type) {
            case 'mortgage':
                return `/balance_sheet/financing/mortgages/${id}`;
            case 'auto_loan':
                return `/balance_sheet/financing/auto_loans/${id}`;
            case 'credit_card':
                return `/balance_sheet/financing/revolvers/credit-cards/${id}`;
            case 'heloc':
                return `/balance_sheet/financing/revolvers/helocs/${id}`;
            default:
                return null;
        }
    };

    useEffect(() => {
        const fetchAssets = async () => {
            try {
                const [physicalResponse, investedResp, cashResp] = await Promise.all([
                    axios.get('/api/balance_sheet/assets/physical_assets'),
                    axios.get('/api/invested_assets/accounts/current_values/'),
                    axios.get('/api/balance_sheet/assets/cash_accounts/current_balances/')
                ]);
                setPhysicalAssets(physicalResponse.data || []);
                setInvestedAccounts((investedResp.data && investedResp.data.accounts) || []);
                setCashAccounts((cashResp.data && cashResp.data.accounts) || []);
            } catch (error) {
                console.error("Error fetching assets:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAssets();
    }, []);

    // Fetch history series for chart
    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const [physHist, invHist, cashHist] = await Promise.all([
                    axios.get('/api/balance_sheet/assets/physical_assets/market_values/history/'),
                    axios.get('/api/invested_assets/accounts/values/history/'),
                    axios.get('/api/balance_sheet/assets/cash_accounts/balances/history/')
                ]);
                const norm = [];
                const pushList = (arr, labelBuilder) => {
                    (arr || [])
                      // Defensive: exclude structures/land if any slipped through
                      .filter(it => it.asset_type_2 !== 'structure' && it.asset_type_2 !== 'land')
                      .forEach(item => {
                        norm.push({
                            id: item.id,
                            asset_type_1: item.asset_type_1 === 'physical_asset' ? 'physical' : item.asset_type_1,
                            asset_type_2: item.asset_type_2,
                            label: labelBuilder ? labelBuilder(item) : (item.description || String(item.id)),
                            // Keep nulls when missing to avoid 0->positive visual jump; backend now clamps
                            series: (item.series || []).map(p => ({
                                date: p.date,
                                value: (p.value === null || p.value === undefined) ? null : parseFloat(p.value)
                            }))
                        });
                      });
                };
                pushList(physHist.data, (it) => it.description || `${it.asset_type_2} ${it.id}`);
                pushList(invHist.data, (it) => it.description || `Investment ${it.id}`);
                pushList(cashHist.data, (it) => it.description || `Cash ${it.id}`);
                setSeries(norm);
            } catch (e) {
                console.error('Error fetching asset histories:', e);
            }
        };
        fetchHistory();
    }, []);

    // Build parent -> children mapping for properties
    const { properties, childrenByProperty } = useMemo(() => {
        const props = [];
        const childrenMap = {};
        for (const a of physicalAssets) {
            if (a.asset_type_2 === 'resi_property') {
                props.push(a);
            }
        }
        for (const a of physicalAssets) {
            if (a.asset_type_2 === 'structure' || a.asset_type_2 === 'land') {
                const rp = a.residential_property;
                const pid = rp && (rp.id || rp); // serializer may send object with id or just id
                if (pid) {
                    if (!childrenMap[pid]) childrenMap[pid] = [];
                    childrenMap[pid].push(a);
                }
            }
        }
        return { properties: props, childrenByProperty: childrenMap };
    }, [physicalAssets]);

    const totalPurchasePrice = useMemo(() => {
        try {
            let sum = 0;
            for (const a of physicalAssets) {
                const ipp = a && a.initial_purchase_price;
                let amt = null;
                if (ipp && typeof ipp === 'object' && ipp.amount != null) {
                    amt = parseFloat(ipp.amount);
                } else if (a && a.initial_purchase_price != null && typeof a.initial_purchase_price !== 'object') {
                    amt = parseFloat(a.initial_purchase_price);
                } else {
                    // try flat field as a fallback
                    const maybe = a?.purchase_price ?? a?.purchase_amount;
                    if (maybe != null) amt = parseFloat(maybe);
                }
                if (!isNaN(amt)) sum += amt;
            }
            return sum;
        } catch (e) {
            return 0;
        }
    }, [physicalAssets]);

    const totalCurrentValue = useMemo(() => {
        let sum = 0;
        try {
            // Only include top-level physical assets (properties, vehicles); exclude structures/land
            for (const a of physicalAssets || []) {
                if (a?.asset_type_2 === 'structure' || a?.asset_type_2 === 'land') continue;
                const v = parseFloat(a?.current_value);
                if (!isNaN(v)) sum += v;
            }
            for (const c of cashAccounts || []) {
                const v = parseFloat(c?.current_balance);
                if (!isNaN(v)) sum += v;
            }
            for (const inv of investedAccounts || []) {
                const v = parseFloat(inv?.current_value);
                if (!isNaN(v)) sum += v;
            }
        } catch (e) { /* ignore */ }
        return sum;
    }, [physicalAssets, cashAccounts, investedAccounts]);

    const totalsByCategory = useMemo(() => {
        let phys = 0;
        let cash = 0;
        let inv = 0;
        try {
            for (const a of physicalAssets || []) {
                if (a?.asset_type_2 === 'structure' || a?.asset_type_2 === 'land') continue;
                const v = parseFloat(a?.current_value);
                if (!isNaN(v)) phys += v;
            }
            for (const c of cashAccounts || []) {
                const v = parseFloat(c?.current_balance);
                if (!isNaN(v)) cash += v;
            }
            for (const i of investedAccounts || []) {
                const v = parseFloat(i?.current_value);
                if (!isNaN(v)) inv += v;
            }
        } catch (e) { /* ignore */ }
        return { physical: phys, cash: cash, invested: inv };
    }, [physicalAssets, cashAccounts, investedAccounts]);

    const getGroupDelta = (type1) => {
        if (!series || series.length === 0) return null;
        
        const filteredSeries = type1 === 'all' 
            ? series 
            : series.filter(s => s.asset_type_1 === type1);
            
        if (filteredSeries.length === 0) return null;

        const getValAt = (points, dateStr) => {
            const found = points.find(p => p.date === dateStr);
            if (found) return found.value;
            const pts = points.filter(p => p.date <= dateStr);
            if (pts.length > 0) {
                // Find the LATEST point among those on or before dateStr
                const sorted = [...pts].sort((a, b) => b.date.localeCompare(a.date));
                return sorted[0].value;
            }
            return 0;
        };

        const now = new Date();
        const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
        const currentMonth = ym(now);

        const subtractMonths = (dateStr, n) => {
            const d = new Date(dateStr);
            d.setMonth(d.getMonth() - n);
            return ym(d);
        };

        let baselineMonth = currentMonth;
        switch (period) {
            case '1M': baselineMonth = subtractMonths(currentMonth, 1); break;
            case '3M': baselineMonth = subtractMonths(currentMonth, 3); break;
            case '6M': baselineMonth = subtractMonths(currentMonth, 6); break;
            case '1Y': baselineMonth = subtractMonths(currentMonth, 12); break;
            case 'YTD': {
                baselineMonth = `${now.getFullYear() - 1}-12-01`;
                break;
            }
            case 'ALL': {
                const allDates = filteredSeries.flatMap(s => s.series.map(p => p.date)).sort();
                baselineMonth = allDates[0] || currentMonth;
                break;
            }
            default: baselineMonth = currentMonth;
        }

        let currentSum = 0;
        let baselineSum = 0;

        filteredSeries.forEach(s => {
            currentSum += getValAt(s.series, currentMonth) || 0;
            baselineSum += getValAt(s.series, baselineMonth) || 0;
        });

        const diff = currentSum - baselineSum;

        if (deltaMode === '$') {
            const absDiff = Math.abs(diff);
            const sign = diff > 0 ? '+' : (diff < 0 ? '-' : '');
            return `${sign}${formatCurrency(absDiff)}`;
        } else {
            if (baselineSum === 0) return diff === 0 ? '0.0%' : '∞%';
            const pct = (diff / baselineSum) * 100;
            const sign = pct > 0 ? '+' : (pct < 0 ? '-' : '');
            return `${sign}${Math.abs(pct).toFixed(1)}%`;
        }
    };

    const allAssetsDelta = useMemo(() => getGroupDelta('all'), [series, period, deltaMode]);
    const physicalDelta = useMemo(() => getGroupDelta('physical'), [series, period, deltaMode]);
    const investedDelta = useMemo(() => getGroupDelta('investment'), [series, period, deltaMode]);
    const cashDelta = useMemo(() => getGroupDelta('cash'), [series, period, deltaMode]);

    if (loading) {
        return <div>Loading assets...</div>;
    }

    // Define table headers to support dynamic total-row placement
    const headers = ['Asset ID', 'Asset Type 1', 'Asset Type 2', 'Description', 'Purchase Price', 'Current Value', 'Purchase Date', 'Financing Source'];
    const totalLabelColIndex = 1; // always the column to the right of Asset ID
    const purchasePriceColIndex = headers.findIndex(h => h.toLowerCase().includes('purchase price'));
    const currentValueColIndex = headers.findIndex(h => h.toLowerCase().includes('current value'));

    const renderFinancing = (asset) => {
        const fin = asset?.financing;
        if (!fin) return '';
        const debts = fin.debt_financing_detailed || [];
        if (debts.length === 0) {
            // If there are no linked debts, render blank cell as requested
            return '';
        }
        return (
            <div style={{display:'flex', flexDirection:'column', gap:'4px'}}>
                {debts.map(d => {
                    const url = getDebtUrl(d);
                    const label = d.lender_name ? `${d.lender_name} (${d.debt_type})` : d.debt_type;
                    return url ? (
                        <Link key={d.id} to={url}>{label}</Link>
                    ) : (
                        <span key={d.id}>{label}</span>
                    );
                })}
            </div>
        );
    };

    const renderAssetRow = (asset, isChild=false) => {
        const purchasePrice = asset?.initial_purchase_price?.amount ?? null;
        const purchaseDate = asset?.initial_purchase_price?.payment_date ?? asset?.purchase_date ?? null;
        const isSubAsset = isChild || asset?.asset_type_2 === 'structure' || asset?.asset_type_2 === 'land';
        return (
            <tr key={`${isChild?'child':'parent'}-${asset.id}`} style={isChild ? { backgroundColor: '#1e3a5f' } : {}}>
                <td style={{ position: 'relative', paddingLeft: isChild ? '24px' : undefined }}>
                    <Link to={getAssetUrl(asset.asset_type_2, asset.id)}>
                        {asset.id}
                    </Link>
                </td>
                <td><Link to="/balance_sheet/assets/physical_assets">Physical Asset</Link></td>
                <td>
                    <Link to={getAssetTypeUrl(asset.asset_type_2)}>
                        {asset.asset_type_2}
                    </Link>
                </td>
                <td>
                    <Link to={getAssetUrl(asset.asset_type_2, asset.id)}>
                        {asset.description || '(no description)'}
                    </Link>
                </td>
                <td>{formatCurrency(purchasePrice)}</td>
                <td>{isSubAsset ? '' : formatCurrency(asset?.current_value)}</td>
                <td>{purchaseDate || 'N/A'}</td>
                <td>{renderFinancing(asset)}</td>
            </tr>
        );
    };

    const renderCashRow = (acct) => {
        const desc = `${acct.lender_name || 'Bank'} ${acct.account_type || ''} *${acct.last4 || ''}`.trim();
        return (
            <tr key={`cash-${acct.id}`}>
                <td>
                    <Link to={`/balance_sheet/assets/cash_assets/${acct.id}`}>{acct.id}</Link>
                </td>
                <td><Link to="/balance_sheet/assets/cash_assets">Cash Asset</Link></td>
                <td><Link to={`/balance_sheet/assets/cash_assets`}>cash_account</Link></td>
                <td><Link to={`/balance_sheet/assets/cash_assets/${acct.id}`}>{desc}</Link></td>
                <td></td>
                <td>{formatCurrency(acct.current_balance)}</td>
                <td>{acct.latest_statement_date || 'N/A'}</td>
                <td></td>
            </tr>
        );
    };

  const renderInvestedRow = (acct) => {
    const desc = `${acct.brokerage || ''} ${acct.account_type || ''} *${String(acct.account_number).slice(-4)}`.trim();
    return (
      <tr key={`inv-${acct.id}`}>
        <td><Link to={`/balance_sheet/assets/invested_assets/${acct.id}`}>{acct.id}</Link></td>
        <td><Link to="/balance_sheet/assets/invested_assets">Invested Asset</Link></td>
        <td><Link to={`/balance_sheet/assets/invested_assets`}>investment_account</Link></td>
        <td><Link to={`/balance_sheet/assets/invested_assets/${acct.id}`}>{desc}</Link></td>
        <td></td>
        <td>{formatCurrency(acct.current_value)}</td>
        <td>{acct.latest_snapshot_date || 'N/A'}</td>
        <td></td>
      </tr>
    );
  };

    return (
        <div className="assets-page" style={{ marginLeft: '40px' }}>
            <div className="page-header-row" style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', marginTop: '20px' }}>
                <div className="header-title-container">
                    <h1 style={{ margin: 0 }}>Assets</h1>
                </div>
                {viewMode === 'tiles' && (
                    <div className="header-ticker" style={{ display: 'flex', alignItems: 'baseline', marginLeft: '24px', paddingLeft: '24px', borderLeft: '1px solid #444' }}>
                        <div className="header-metric" style={{ marginRight: '20px' }}>
                            <span className="metric-label" style={{ fontSize: '14px', color: '#bbbbbb', marginRight: '8px' }}>Total Assets</span>
                            <span className="metric-value" style={{ fontSize: '20px', fontWeight: 600, color: '#ffffff' }}>{formatCurrency(totalCurrentValue)}</span>
                            {allAssetsDelta && (
                                <span className={`delta-${allAssetsDelta.startsWith('+') ? 'up' : allAssetsDelta.startsWith('-') ? 'down' : 'neutral'}`} style={{ marginLeft: '8px', fontSize: '16px', fontWeight: 600 }}>
                                    {allAssetsDelta}
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="tabs-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div className="tab-bar">
                    <button className={`tab-btn ${viewMode === 'tiles' ? 'active' : ''}`} onClick={() => setViewMode('tiles')}>Tiles</button>
                    <button className={`tab-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>Table</button>
                </div>
                {viewMode === 'tiles' && (
                    <div className="controls-row" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                        <div className="period-toggles" style={{ display: 'flex', background: '#1e1e1e', borderRadius: '6px', padding: '2px' }}>
                            {['1M', '3M', '6M', '1Y', 'YTD', 'ALL'].map(p => (
                                <button
                                    key={p}
                                    onClick={() => setPeriod(p)}
                                    style={{
                                        padding: '6px 12px',
                                        border: 'none',
                                        background: period === p ? '#3b82f6' : 'transparent',
                                        color: period === p ? '#fff' : '#aaa',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                        <div className="mode-toggles" style={{ display: 'flex', background: '#1e1e1e', borderRadius: '6px', padding: '2px' }}>
                            {['%', '$'].map(m => (
                                <button
                                    key={m}
                                    onClick={() => setDeltaMode(m)}
                                    style={{
                                        padding: '6px 12px',
                                        border: 'none',
                                        background: deltaMode === m ? '#3b82f6' : 'transparent',
                                        color: deltaMode === m ? '#fff' : '#aaa',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {m}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {viewMode === 'table' && (
                <>
                    {/* Chart: Assets value over time with aggregation toggles */}
                    {series.length > 0 && (
                        <div style={{ marginRight: '40px' }}>
                            <AssetValueChart series={series} defaultMode="aggregate" />
                        </div>
                    )}

                    <table className="all-assets-table" style={{ marginTop: '32px' }}>
                        <thead>
                            <tr>
                                {headers.map((h, idx) => (
                                    <th key={`hdr-${idx}`}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {(physicalAssets.length + cashAccounts.length + investedAccounts.length) > 0 ? (
                                <>
                                    {/* Physical: Properties with children */}
                                    {properties.map(p => {
                                        const kids = childrenByProperty[p.id] || [];
                                        const isOpen = !!expanded[p.id];
                                        return (
                                            <React.Fragment key={`prop-${p.id}`}>
                                                <tr>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                                            <Link to={getAssetUrl(p.asset_type_2, p.id)}>{p.id}</Link>
                                                            {(kids.length > 0) ? (
                                                                <button
                                                                className="btn btn-xs btn-outline-secondary"
                                                                onClick={() => setExpanded(prev => ({...prev, [p.id]: !prev[p.id]}))}
                                                                title={isOpen ? 'Collapse' : 'Expand'}
                                                                style={{ padding: '0 6px', lineHeight: 1.2 }}
                                                                >
                                                                {isOpen ? '▼' : '►'}
                                                                </button>
                                                            ) : (
                                                                <span style={{ width: 16, display: 'inline-block' }} />
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td><Link to="/balance_sheet/assets/physical_assets">Physical Asset</Link></td>
                                                    <td><Link to={getAssetTypeUrl(p.asset_type_2)}>{p.asset_type_2}</Link></td>
                                                    <td>
                                                        <Link to={getAssetUrl(p.asset_type_2, p.id)}>{p.description || '(no description)'}</Link>
                                                    </td>
                                                    <td>{formatCurrency(p?.initial_purchase_price?.amount ?? null)}</td>
                                                    <td>{formatCurrency(p?.current_value)}</td>
                                                    <td>{(p?.initial_purchase_price?.payment_date ?? p?.purchase_date) || 'N/A'}</td>
                                                    <td>{renderFinancing(p)}</td>
                                                </tr>
                                                {isOpen && kids.map(child => renderAssetRow(child, true))}
                                            </React.Fragment>
                                        );
                                    })}
                                    {/* Physical: other top-level (e.g., vehicles) */}
                                    {physicalAssets.filter(a => a.asset_type_2 === 'vehicle').map(v => renderAssetRow(v))}

                                    {/* Invested accounts */}
                                    {investedAccounts.map(acct => renderInvestedRow(acct))}
                                    {/* Cash accounts */}
                                    {cashAccounts.map(acct => renderCashRow(acct))}
                                </>
                            ) : (
                                <tr>
                                    <td colSpan={headers.length} style={{ textAlign: 'center' }}>No assets found.</td>
                                </tr>
                            )}
                        </tbody>
                        {(physicalAssets.length + cashAccounts.length + investedAccounts.length) > 0 && (
                            <tfoot>
                                <tr>
                                    {headers.map((h, idx) => {
                                        const isLabelCol = idx === totalLabelColIndex;
                                        const isPurchasePriceCol = idx === purchasePriceColIndex;
                                        const isCurrentValueCol = idx === currentValueColIndex;
                                        let content = '';
                                        if (isLabelCol) content = 'Total';
                                        if (isPurchasePriceCol) content = formatCurrency(totalPurchasePrice);
                                        if (isCurrentValueCol) content = formatCurrency(totalCurrentValue);
                                        const style = {
                                        border: 'none',
                                        borderTop: '1px solid #374151',
                                        backgroundColor: 'transparent'
                                        };
                                        if (isPurchasePriceCol || isCurrentValueCol) { style.textAlign = 'left'; style.fontWeight = 600; }
                                        if (isLabelCol) { style.fontWeight = 600; }
                                        return (
                                            <td key={`tot-${idx}`} style={style}>{content}</td>
                                        );
                                    })}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </>
            )}

            {viewMode === 'tiles' && (
                <div className="tiles-container">
                    <div className="tiles-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px', paddingRight: '40px' }}>
                        <Link to="/balance_sheet/assets/physical_assets" className="tile-card-link" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="tile-card" style={{ background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '24px', transition: 'transform 0.2s, box-shadow 0.2s' }}>
                                <div className="tile-icon" style={{ fontSize: '32px', marginBottom: '16px' }}>🏠</div>
                                <div className="tile-title" style={{ fontSize: '20px', fontWeight: 700, color: '#ffffff', marginBottom: '8px' }}>Physical Assets</div>
                                <div className="tile-subtitle" style={{ fontSize: '14px', color: '#aaaaaa', marginBottom: '20px' }}>Real Estate, Vehicles, etc.</div>
                                <div className="tile-metrics" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div>
                                        <span className="metric-label" style={{ display: 'block', fontSize: '12px', color: '#bbbbbb', textTransform: 'uppercase' }}>Total Value</span>
                                        <span className="metric-value" style={{ display: 'block', fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>{formatCurrency(totalsByCategory.physical)}</span>
                                    </div>
                                    <div>
                                        <span className="metric-label" style={{ display: 'block', fontSize: '12px', color: '#bbbbbb', textTransform: 'uppercase' }}>Change ({period})</span>
                                        <span className={`metric-value delta-${(physicalDelta || '').startsWith('+') ? 'up' : (physicalDelta || '').startsWith('-') ? 'down' : 'neutral'}`} style={{ display: 'block', fontSize: '24px', fontWeight: 600 }}>
                                            {physicalDelta || '—'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Link>

                        <Link to="/balance_sheet/assets/invested_assets" className="tile-card-link" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="tile-card" style={{ background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '24px', transition: 'transform 0.2s, box-shadow 0.2s' }}>
                                <div className="tile-icon" style={{ fontSize: '32px', marginBottom: '16px' }}>📈</div>
                                <div className="tile-title" style={{ fontSize: '20px', fontWeight: 700, color: '#ffffff', marginBottom: '8px' }}>Invested Assets</div>
                                <div className="tile-subtitle" style={{ fontSize: '14px', color: '#aaaaaa', marginBottom: '20px' }}>Brokerage, Retirement, etc.</div>
                                <div className="tile-metrics" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div>
                                        <span className="metric-label" style={{ display: 'block', fontSize: '12px', color: '#bbbbbb', textTransform: 'uppercase' }}>Total Value</span>
                                        <span className="metric-value" style={{ display: 'block', fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>{formatCurrency(totalsByCategory.invested)}</span>
                                    </div>
                                    <div>
                                        <span className="metric-label" style={{ display: 'block', fontSize: '12px', color: '#bbbbbb', textTransform: 'uppercase' }}>Change ({period})</span>
                                        <span className={`metric-value delta-${(investedDelta || '').startsWith('+') ? 'up' : (investedDelta || '').startsWith('-') ? 'down' : 'neutral'}`} style={{ display: 'block', fontSize: '24px', fontWeight: 600 }}>
                                            {investedDelta || '—'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Link>

                        <Link to="/balance_sheet/assets/cash_assets" className="tile-card-link" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="tile-card" style={{ background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '24px', transition: 'transform 0.2s, box-shadow 0.2s' }}>
                                <div className="tile-icon" style={{ fontSize: '32px', marginBottom: '16px' }}>💰</div>
                                <div className="tile-title" style={{ fontSize: '20px', fontWeight: 700, color: '#ffffff', marginBottom: '8px' }}>Cash Assets</div>
                                <div className="tile-subtitle" style={{ fontSize: '14px', color: '#aaaaaa', marginBottom: '20px' }}>Checking, Savings, CDs</div>
                                <div className="tile-metrics" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div>
                                        <span className="metric-label" style={{ display: 'block', fontSize: '12px', color: '#bbbbbb', textTransform: 'uppercase' }}>Total Value</span>
                                        <span className="metric-value" style={{ display: 'block', fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>{formatCurrency(totalsByCategory.cash)}</span>
                                    </div>
                                    <div>
                                        <span className="metric-label" style={{ display: 'block', fontSize: '12px', color: '#bbbbbb', textTransform: 'uppercase' }}>Change ({period})</span>
                                        <span className={`metric-value delta-${(cashDelta || '').startsWith('+') ? 'up' : (cashDelta || '').startsWith('-') ? 'down' : 'neutral'}`} style={{ display: 'block', fontSize: '24px', fontWeight: 600 }}>
                                            {cashDelta || '—'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </div>
                </div>
            )}

            <style jsx>{`
                .assets-page {
                    margin-left: clamp(20px, 4vw, 40px);
                    margin-right: clamp(20px, 4vw, 40px);
                    padding-bottom: 40px;
                }
                .tab-bar {
                    display: inline-flex;
                    align-items: flex-end;
                    gap: 4px;
                    border-bottom: 1px solid #374151;
                }
                .tab-btn {
                    background: #111827;
                    color: #e5e7eb;
                    border: 1px solid #374151;
                    border-bottom-color: #374151;
                    padding: 6px 14px;
                    border-top-left-radius: 8px;
                    border-top-right-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                }
                .tab-btn.active {
                    background: #1f2937;
                    border-color: #4b5563;
                    border-bottom-color: transparent;
                    color: #ffffff;
                    position: relative;
                    top: 1px;
                }
                .tile-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(0,0,0,0.4);
                }
                .delta-up {
                    color: #4ade80 !important;
                }
                .delta-down {
                    color: #f87171 !important;
                }
                .delta-neutral {
                    color: #9ca3af !important;
                }
                .all-assets-table {
                    width: calc(100% - 40px);
                    border-collapse: collapse;
                }
                .all-assets-table th {
                    text-align: left;
                    padding: 12px 8px;
                    border-bottom: 2px solid #374151;
                    color: #9ca3af;
                    font-weight: 600;
                    font-size: 14px;
                }
                .all-assets-table td {
                    padding: 12px 8px;
                    border-bottom: 1px solid #1f2937;
                }
                .period-toggles button:hover, .mode-toggles button:hover {
                    color: #fff !important;
                }
            `}</style>
        </div>
    );
};

export default AssetsPage;